import React from 'react';
import { Box } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { Chip } from 'cprime-foundational-components';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { formatDataValue } from 'utils/formatFns';
import { booleanToYN, generateTestId } from 'utils/general';
import { chipColorMap } from 'utils/colorMappers';
import { getChipSx } from '../utils/utils';

export const ASSIGNED_TLS_FACILITY_LABEL = 'Assigned TLS Facility';

export const logisticsFields = {
  actualFQADate: {
    label: 'Act. FQA Date',
    getValue: (data) => data?.logistics?.actualFQADateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  actualVPCProcessingDate: {
    label: 'Act. VPC Processing Date',
    getValue: (data) => data?.logistics?.inProcessDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  assignedTLSFacility: {
    label: ASSIGNED_TLS_FACILITY_LABEL,
    getValue: (data) => ({
      assignedTlsFacility: data?.logistics?.assignedTLSFacility,
      lifeCycleStatus: data?.main?.header?.lifeCycleStatus,
      currentVDCLongName: data?.logistics?.currentVDCLongName,
    }),
    formatValue: ({ assignedTlsFacility, lifeCycleStatus, currentVDCLongName }, options = {}) => {
      const allowedForValue = ['VPC Stock', 'Other VPC Stock'].includes(lifeCycleStatus);
      const isOtherVPC = ['In Transit to Other VPC', 'Other VPC Stock'].includes(lifeCycleStatus);

      if (!assignedTlsFacility || !allowedForValue || isOtherVPC) {
        return null;
      }

      const vpcTooltipText = currentVDCLongName || null;

      return (
        <TooltipWrapper
          disableHoverListener={!vpcTooltipText}
          tooltipText={
            vpcTooltipText
              ? `${ASSIGNED_TLS_FACILITY_LABEL} | ${vpcTooltipText}`
              : ASSIGNED_TLS_FACILITY_LABEL
          }
        >
          <Box display="flex" alignItems="center">
            <Chip
              data-testid={generateTestId(
                'VehicleRecord',
                'VehicleDetailsHeader',
                'assigned-tls-facility-chip',
              )}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value={assignedTlsFacility}
                    fieldId="vehicle-hero-assigned-tls-facility"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={
                options.isMobile ? (
                  <LocationOn color={chipColorMap.lifecycleChipBackground} />
                ) : null
              }
              color={chipColorMap.lifecycleChipText}
              backgroundColor={chipColorMap.lifecycleChipBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  currentVDC: {
    label: 'Current VDC',
    getValue: (data) => data?.logistics?.currentVDC,
    formatValue: (value) => formatDataValue({ value }),
  },
  currentVDCLongName: {
    label: 'Current VDC Long Name',
    getValue: (data) => data?.logistics?.currentVDCLongName,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedFQADate: {
    label: 'Est. FQA Date',
    getValue: (data) => data?.logistics?.estimatedFQADateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  freezeDate: {
    label: 'Freeze Date',
    getValue: (data) =>
      data?.logistics?.processingInfo?.portProcessesList?.find(
        (item) => item.simulStatusDescription === 'FROZEN',
      )?.portSimulationActivityDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  orderSource: {
    label: 'Order Source',
    getValue: (data) => data?.logistics?.postedOrderTypeName,
    formatValue: (value) => formatDataValue({ value }),
  },
  portEntryShortName: {
    label: 'Port Entry Short Name',
    getValue: (data) => data?.logistics?.portEntryShortName,
    formatValue: (value) => formatDataValue({ value }),
  },
  processingStatus: {
    label: 'Processing Status',
    getValue: (data) =>
      data?.logistics?.processingInfo?.portProcessesList?.[0]?.simulStatusDescription,
    formatValue: (value) => (value ? formatDataValue({ value }) : null),
  },
  processingTypeCodeDescription: {
    label: 'Processing Type Code | Description',
    getValue: (data) => ({
      specialServiceCode: data?.logistics?.processingInfo?.specialServiceCode,
      specialServiceDescription: data?.logistics?.processingInfo?.specialServiceDescription,
    }),
    formatValue: ({ specialServiceCode, specialServiceDescription }) => {
      if (!specialServiceCode && !specialServiceDescription) {
        return null;
      }
      const formattedCode = formatDataValue({ value: specialServiceCode });
      const formattedDescription = formatDataValue({ value: specialServiceDescription });
      return `${formattedCode} | ${formattedDescription}`;
    },
  },
  serviceCodes: {
    label: 'Service Codes',
    getValue: (data) => data?.logistics?.processingInfo?.specialServiceCode,
    formatValue: (value) => value || null,
  },
  specialServiceRequest: {
    label: 'Special Service Request',
    getValue: (data) => ({
      specialServiceCode: data?.logistics?.processingInfo?.specialServiceCode,
      specialServiceDescription: data?.logistics?.processingInfo?.specialServiceDescription,
    }),
    formatValue: ({ specialServiceCode, specialServiceDescription }) => {
      const formattedCode = formatDataValue({ value: specialServiceCode });
      const formattedDescription = formatDataValue({ value: specialServiceDescription });
      return `${formattedCode} | ${formattedDescription}`;
    },
  },
  ucService: {
    label: 'UC Service',
    getValue: (data) => data?.logistics?.ucServiceIndicator,
    formatValue: (value) => booleanToYN(value),
  },
  vdc: {
    label: 'VDC',
    getValue: (data) => ({
      vdc: data?.logistics?.currentVDC,
      name: data?.logistics?.currentVDCShortName,
    }),
    formatValue: (value) => {
      const vdc = formatDataValue({ value: value.vdc });
      const name = formatDataValue({ value: value.name });
      return `${vdc} | ${name}`;
    },
  },
};
